import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Empty, Select, Space, Table, Tag } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { ProjectSelector } from '@/components/project/ProjectSelector';
import pmvService, { type PmvRequest } from '@/services/pmv.service';
import { useProjects } from '@/hooks/useProjects';
import { buildProjectIdToLabelMap, resolveProjectDisplayFromMap } from '@/utils/pmvDisplay';
import { hireTableCellEllipsis, hireTableLinkBtnStyle } from '@/utils/pmvHireUi';

const STATUS_OPTS = [
  'DRAFT',
  'SUBMITTED',
  'APPROVED',
  'MATCHING',
  'FULFILLING',
  'FULFILLED',
  'REJECTED',
  'CANCELLED',
  'CLOSED',
].map((v) => ({ value: v, label: v }));

const PmvRequestRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string | undefined>();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { data: projects = [] } = useProjects();
  const projectLabels = useMemo(() => buildProjectIdToLabelMap(projects), [projects]);

  const listQ = useQuery({
    queryKey: ['pmv-requests', page, status, projectId],
    queryFn: () =>
      pmvService.listRequests({
        page,
        pageSize: 50,
        status,
        project_id: projectId,
      }),
  });

  const items = listQ.data?.data ?? [];

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Equipment Requests"
        subtitle="Capability demand, matching and fulfilment — not a warehouse stock issue"
        primaryActions={
          <Space wrap>
            <Button type="primary" onClick={() => navigate('/asset/pmv/requests/new')}>
              New request
            </Button>
            <Button onClick={() => navigate('/asset/pmv/operations')}>Operations</Button>
            <Button onClick={() => navigate('/asset/pmv/control-tower')}>Command Center</Button>
          </Space>
        }
        extra={
          <Space wrap>
            <div style={{ minWidth: 200 }}>
              <ProjectSelector
                value={projectId}
                onChange={(v) => {
                  setProjectId(v || undefined);
                  setPage(1);
                }}
                allowClear
                placeholder="Project"
              />
            </div>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 160 }}
              value={status}
              onChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
              options={STATUS_OPTS}
            />
          </Space>
        }
      />
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="Requests express capability need. Fulfilment allocates PMV units — they do not create hire commercial terms."
      />
      {!listQ.isLoading && items.length === 0 ? (
        <Empty description="No equipment requests match these filters">
          <Button type="primary" onClick={() => navigate('/asset/pmv/requests/new')}>
            Create request
          </Button>
        </Empty>
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={listQ.isLoading}
          dataSource={items}
          pagination={{
            current: page,
            total: listQ.data?.pagination?.total ?? 0,
            pageSize: 50,
            onChange: setPage,
          }}
          scroll={{ x: 900 }}
          tableLayout="fixed"
          columns={[
            {
              title: 'Request',
              dataIndex: 'request_number',
              width: 160,
              ellipsis: true,
              render: (v: string, r: PmvRequest) => (
                <div style={hireTableCellEllipsis}>
                  <Button
                    type="link"
                    style={hireTableLinkBtnStyle}
                    onClick={() => navigate(`/asset/pmv/requests/${r.id}`)}
                  >
                    {v}
                  </Button>
                </div>
              ),
            },
            {
              title: 'Project',
              dataIndex: 'project_id',
              width: 220,
              ellipsis: true,
              render: (pid: string) => resolveProjectDisplayFromMap(projectLabels, pid),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 130,
              render: (v: string) => <Tag>{v}</Tag>,
            },
            { title: 'Priority', dataIndex: 'priority', width: 100 },
            {
              title: 'Submitted',
              dataIndex: 'submitted_at',
              width: 160,
              render: (v: string | null) => (v ? String(v).slice(0, 16) : '—'),
            },
          ]}
        />
      )}
    </WorkspaceLayout>
  );
};

export default PmvRequestRegisterPage;
